import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import TaskHistory from '../tasks/TaskHistory';
import { API, stationLabel, serviceLabel } from '../../utils/kitchenTasks';
import { formatDateFR, formatDateTimeFR } from '../../utils/dateFormat';
import { stateMeta, sourceLabel, rawStatusLabel, qty, who } from './reportUtils';

const Info = ({ label, value, testId }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">{label}</p>
    <p className="text-sm text-gray-900 break-words" data-testid={testId}>{value || '—'}</p>
  </div>
);
const when = (iso) => (iso ? formatDateTimeFR(iso) : '');

const ReportDetailDialog = ({ row, open, onOpenChange }) => {
  const [full, setFull] = useState(null);
  useEffect(() => {
    if (!row) { setFull(null); return undefined; }
    let cancelled = false;
    axios.get(`${API}/reports/mise-en-place/${row.source}/${row.id}`).then(r => { if (!cancelled) setFull(r.data.data); }).catch(() => { if (!cancelled) setFull(row); });
    return () => { cancelled = true; };
  }, [row]);
  if (!row) return null;
  const r = full || row;
  const st = stateMeta(r.state);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-gray-900">{r.title}</DialogTitle>
          <DialogDescription className="text-gray-500">{formatDateFR(r.task_date)} · {serviceLabel(r.service)} · {stationLabel(r.category_name)} · {sourceLabel(r.source)}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${st.cls}`} data-testid="report-detail-state">{st.label}</span>
          <span className="text-xs text-gray-500">Statut détaillé : {rawStatusLabel(r.status)}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Info label="Quantité demandée" value={qty(r.planned_quantity, r.unit)} testId="report-detail-planned" />
          <Info label="Quantité produite" value={qty(r.actual_quantity, r.actual_unit)} testId="report-detail-actual" />
          <Info label="Affectée à" value={who(r) || 'Non affectée'} testId="report-detail-assignees" />
          <Info label={r.state === 'non_realisee' ? 'Déclarée non réalisée par' : 'Exécutée par'} value={r.executed_by_name} testId="report-detail-executor" />
          <Info label="Créée par" value={r.created_by_name ? `${r.created_by_name}${when(r.created_at) ? ` · ${when(r.created_at)}` : ''}` : when(r.created_at)} />
          <Info label="Commencée le" value={when(r.started_at)} />
          <Info label="Terminée le" value={when(r.completed_at)} testId="report-detail-completed-at" />
          <Info label="Heure limite" value={r.due_time} />
          <Info label="Priorité" value={r.priority} />
          {r.not_done_reason && <Info label="Motif de non-réalisation" value={`${r.not_done_reason}${when(r.not_done_at) ? ` · ${when(r.not_done_at)}` : ''}`} testId="report-detail-reason" />}
          {r.collaborator_comment && <Info label="Commentaire collaborateur" value={r.collaborator_comment} />}
          {r.chef_comment && <Info label="Commentaire chef" value={r.chef_comment} />}
          {r.description && <Info label="Consigne" value={r.description} />}
        </div>
        {r.source === 'tache' && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 font-bold mb-2">Historique ({(r.history || []).length})</p>
            {!full ? <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={14} className="animate-spin" />Chargement…</div> : <TaskHistory history={r.history} />}
          </div>
        )}
        {r.source === 'swipe' && <p className="text-xs text-gray-400">Les mises en place Swipe n'ont pas d'historique détaillé : seuls l'état et l'auteur de la validation sont enregistrés.</p>}
      </DialogContent>
    </Dialog>
  );
};

export default ReportDetailDialog;
